import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { Patient, PromptType, Schedule, ConsultationRecordListItem } from "common/models/types";
import { CreateScheduleParams, UpdateScheduleParams } from "api/scheduleService";
import ScheduleSection from "components/organisms/ScheduleSection";
import ConsultationRecordSection from "components/organisms/ConsultationRecordSection";
import AIReportHistoryList from "components/organisms/AIReportHistoryList";
import UnlinkedDrugIdentifications from "components/organisms/UnlinkedDrugIdentifications";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import { IconName } from "components/atoms/Icon/IconNames";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import { calculateAge } from "utils/TimeUtil";
import PrescriptionHistory from "components/organisms/PrescriptionHistory/PrescriptionHistory";
import Input from "components/atoms/Input";
import parse from "html-react-parser";
import OTCHistory from "components/organisms/OTCHistory/OTCHistory";
import FloatingButton from "components/atoms/FloatingButton";
import InfoIconButton from "components/atoms/InfoIconButton";
import { Spinner, Static } from "components/atoms";
import EditPatientModal from "components/molecules/EditPatientModal";
import { ToastManager } from "components/organisms/CommonUI";
import { PrescriptionHospitalGroup } from "common/models/types";
import { NhisTransactionStatus } from "types/nhis";
import NhisRequestStatusList from "components/organisms/NhisRequestStatusList";
import MedicineCountGauge from "components/molecules/MedicineCountGauge";
import { GAUGE_GREEN_MAX } from "components/molecules/MedicineCountGauge/medicineCountStatus";

// props 타입 정의
interface PatientDetailTemplateProps {
  patient: Patient;
  referenceChecker?: {
    [id: string]: {
      count: number;
      totalLength?: number;
      totalBytes?: number;
    };
  };
  hospitalGroupedPrescriptions?: PrescriptionHospitalGroup[] | null;
  onToggleViewMode?: () => void;
  onEditPatient?: (updatedPatient: Patient) => void;
  onEditDiseases?: (diseases: string) => void;
  onEditAllergies?: (allergies: string) => void;
  onEditConsultationNotes?: (consultationNotes: string) => void;
  onEditMemo?: (memo: string) => void;
  onEditPrescription?: (groupId?: string) => void;
  onDeletePrescription?: (groupId: string) => void;
  onEditOtc?: (prescriptionId?: number) => void;
  onDeleteOtc?: (prescriptionId: number) => void;
  onEditHff?: (prescriptionId?: number) => void;
  onDeleteHff?: (prescriptionId: number) => void;
  onAnalysisStart?: () => void;
  onAdminAnalysisStart?: () => void;
  onDeletePatient?: () => void;
  onExportMedicationHistory?: () => void;
  onOpenHistoryViewer?: () => void;
  loadingProductLabel?: boolean;
  isPremiumUser?: boolean;
  prescriptionStats?: {
    totalPrescriptions: number;
    totalMedicines: number;
    uniqueCount: number;
    referenceCount: number;
  };
  schedules?: Schedule[];
  onCreateSchedule?: (data: CreateScheduleParams) => void;
  onUpdateSchedule?: (scheduleId: number, data: UpdateScheduleParams) => void;
  onDeleteSchedule?: (scheduleId: number) => void;
  onSelectConsultationRecord?: (recordId: number) => void;
  /** 로그인한 약사 id — 「AI 검토 이력」 줄의 체크리스트 칸은 본인 보고서에만(결정 20). */
  currentUserId?: string;
  /** 체크리스트 칸 「작성」 — 그 보고서로 새 기록. 본인 환자 화면일 때만 Page가 넘긴다(설계 결정 8·20). */
  onWriteChecklist?: (reviewId: number) => void;
  /** 체크리스트 칸 "작성됨" — 그 기록 열기(오늘이면 수정, 아니면 읽기). 본인 환자 화면일 때만(설계 결정 8·20). */
  onOpenChecklist?: (recordId: number) => void;
  onDeleteDrugIdentification?: (id: number) => void;
  nhisRequests?: NhisTransactionStatus[];
  onCancelNhisRequest?: (transactionId: string) => void;
}

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Gray")?.[10]};
  position: relative;

  h2 {
    margin: 0;
  }

  h3 {
    margin: 0 0 2px 0;
  }
`;

const PatientHeader = styled.div`
  padding: 2px 12px;
  margin-bottom: 24px;
`;

const PatientOptionsContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const PatientOptionsDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 1000;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  overflow: hidden;
`;

const PatientOptionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")[10]};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
  }
`;

const FixedContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
  pointer-events: none; /* 컨테이너 자체는 클릭 이벤트를 차단하지 않음 */

  /* 플로팅 버튼만 클릭 가능하게 설정 */
  & > * {
    pointer-events: auto;
  }
`;

const SectionGroup = styled.div`
  margin: 48px 0 0;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.04);
  &:first-of-type {
    margin-top: 0;
  }
`;

const SectionGroupTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
  margin-bottom: 24px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
`;

const Card = styled.div`
  position: relative;
  & + & {
    margin-top: 40px;
  }
`;


const InfoBox = styled.div<{ isEditing?: boolean }>`
  background: ${(props) =>
    props.isEditing ? COLOR_SYSTEM.get("Gray")[20] : COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid
    ${(props) => (props.isEditing ? COLOR_SYSTEM.get("Gray")[100] : COLOR_SYSTEM.get("Gray")[50])};
  border-radius: 8px;
  padding: 18px 20px;
  min-height: 48px;
  margin-top: 8px;
  cursor: text;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Gray")[100]};
  }
`;

const EditableInput = styled(Input)`
  border: none;
  background: transparent;
  padding: 0;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const EditableTextarea = styled.textarea`
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  line-height: 1.5;
  min-height: 20px;

  &::placeholder {
    color: ${COLOR_SYSTEM.get("Gray")[300]};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
`;

const FormField = styled.div`
  max-width: 200px;
`;

const Select = styled.select`
  width: 100%;
  padding: 11.5px 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")[10]};
  color: ${COLOR_SYSTEM.get("Gray")[800]};
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Gray")[100]};
    background-color: ${COLOR_SYSTEM.get("Gray")[20]};
  }
`;

function formatKoreanDate(dateInput?: string | Date) {
  if (!dateInput) return "-";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return String(dateInput);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}

const PatientDetailTemplate: React.FC<PatientDetailTemplateProps> = ({
  patient,
  referenceChecker,
  hospitalGroupedPrescriptions,
  onToggleViewMode,
  onEditPatient,
  onEditDiseases,
  onEditAllergies,
  onEditConsultationNotes,
  onEditMemo,
  onEditPrescription,
  onDeletePrescription,
  onEditOtc,
  onDeleteOtc,
  onEditHff,
  onDeleteHff,
  onAnalysisStart,
  onAdminAnalysisStart,
  onDeletePatient,
  onExportMedicationHistory,
  onOpenHistoryViewer,
  loadingProductLabel,
  isPremiumUser,
  prescriptionStats,
  schedules,
  onCreateSchedule,
  onUpdateSchedule,
  onDeleteSchedule,
  onSelectConsultationRecord,
  currentUserId,
  onWriteChecklist,
  onOpenChecklist,
  onDeleteDrugIdentification,
  nhisRequests,
  onCancelNhisRequest,
}) => {
  const prescriptionGroups = patient.prescriptionGroups;
  const otcList = patient.otc || [];
  const hffList = patient.hff || [];
  const age = calculateAge(patient.birthDate);

  // 개별 필드 편집 상태
  const [editingField, setEditingField] = useState<string | null>(null);

  // 환자 옵션 드롭다운 상태
  const [isPatientOptionsOpen, setIsPatientOptionsOpen] = useState<boolean>(false);

  // 환자 정보 수정 모달 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".patient-options-container")) {
        setIsPatientOptionsOpen(false);
      }
    };

    if (isPatientOptionsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPatientOptionsOpen]);

  // 저장 상태 추적
  const [savingFields, setSavingFields] = useState<string[]>([]);

  // 타이머 참조
  const saveTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // textarea 참조들
  const consultationNotesRef = useRef<HTMLTextAreaElement>(null);
  const memoRef = useRef<HTMLTextAreaElement>(null);
  const diseasesRef = useRef<HTMLTextAreaElement>(null);
  const allergiesRef = useRef<HTMLTextAreaElement>(null);

  // 개별 필드 값들
  const [birthYear, setBirthYear] = useState<string>(
    patient.birthDate ? new Date(patient.birthDate).getFullYear().toString() : ""
  );
  const [gender, setGender] = useState<string>(patient.gender || "");
  const [consultationNotes, setConsultationNotes] = useState<string>(
    patient.consultationNotes || ""
  );
  const [memo, setMemo] = useState<string>(patient.memo || "");
  const [diseases, setDiseases] = useState<string>(patient.diseases || "");
  const [allergies, setAllergies] = useState<string>(patient.allergies || "");

  // 원본 값들 추적 (변경사항 감지용)
  const [originalValues, setOriginalValues] = useState({
    birthYear: patient.birthDate ? new Date(patient.birthDate).getFullYear().toString() : "",
    gender: patient.gender || "",
    consultationNotes: patient.consultationNotes || "",
    memo: patient.memo || "",
    diseases: patient.diseases || "",
    allergies: patient.allergies || "",
  });

  // textarea 높이 자동 조절
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  };

  // 모든 textarea 높이 조정
  const adjustAllTextareaHeights = useCallback(() => {
    // [2024-01-XX] 결정사항: 환자 변경 시 모든 textarea의 높이를 텍스트 내용에 맞게 자동 조정
    setTimeout(() => {
      if (consultationNotesRef.current) {
        adjustTextareaHeight(consultationNotesRef.current);
      }
      if (memoRef.current) {
        adjustTextareaHeight(memoRef.current);
      }
      if (diseasesRef.current) {
        adjustTextareaHeight(diseasesRef.current);
      }
      if (allergiesRef.current) {
        adjustTextareaHeight(allergiesRef.current);
      }
    }, 0);
  }, []);

  // patient가 변경될 때 상태 및 원본값 업데이트
  useEffect(() => {
    const newValues = {
      birthYear: patient.birthDate ? new Date(patient.birthDate).getFullYear().toString() : "",
      gender: patient.gender || "",
      consultationNotes: patient.consultationNotes || "",
      memo: patient.memo || "",
      diseases: patient.diseases || "",
      allergies: patient.allergies || "",
    };

    setBirthYear(newValues.birthYear);
    setGender(newValues.gender);
    setConsultationNotes(newValues.consultationNotes);
    setMemo(newValues.memo);
    setDiseases(newValues.diseases);
    setAllergies(newValues.allergies);

    // 원본값도 업데이트
    setOriginalValues(newValues);

    // 모든 textarea 높이 조정
    adjustAllTextareaHeights();
  }, [patient, adjustAllTextareaHeights]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      saveTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      saveTimeouts.current.clear();
    };
  }, []);

  // 페이지 이탈 시 자동저장
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 모든 타이머 정리하고 즉시 저장
      Array.from(saveTimeouts.current.entries()).forEach(([fieldName, timeout]) => {
        clearTimeout(timeout);
        handleImmediateSave(fieldName);
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // 컴포넌트 언마운트 시에도 즉시 저장
      Array.from(saveTimeouts.current.entries()).forEach(([fieldName, timeout]) => {
        clearTimeout(timeout);
        handleImmediateSave(fieldName);
      });
    };
  }, []);

  // 즉시 저장 핸들러 (페이지 이탈 시 사용)
  const handleImmediateSave = (fieldName: string) => {
    if (savingFields.includes(fieldName)) return; // 이미 저장 중이면 중단

    performSave(fieldName);
  };

  // 변경사항 확인 함수
  const hasChanges = (fieldName: string): boolean => {
    switch (fieldName) {
      case "birthYear":
        return birthYear !== originalValues.birthYear;
      case "gender":
        return gender !== originalValues.gender;
      case "consultationNotes":
        return consultationNotes !== originalValues.consultationNotes;
      case "memo":
        return memo !== originalValues.memo;
      case "diseases":
        return diseases !== originalValues.diseases;
      case "allergies":
        return allergies !== originalValues.allergies;
      default:
        return false;
    }
  };

  // 실제 저장 로직
  const performSave = useCallback(
    async (fieldName: string) => {
      // 변경사항이 없으면 저장하지 않음
      if (!hasChanges(fieldName)) {
        console.log(`${fieldName}: 변경사항이 없어서 저장을 건너뜁니다.`);
        return;
      }

      // 저장 상태 추가
      setSavingFields((prev) => (prev.includes(fieldName) ? prev : [...prev, fieldName]));

      try {
        switch (fieldName) {
          case "birthYear":
          case "gender":
            if (onEditPatient) {
              let updatedPatient: Patient = {
                id: patient.id,
                birthDate: birthYear
                  ? new Date(parseInt(birthYear), 0, 1).toISOString()
                  : patient.birthDate,
                gender: gender !== undefined ? gender : patient.gender,
              };
              await onEditPatient(updatedPatient);

              // 저장 성공 시 원본값 업데이트
              setOriginalValues((prev) => ({
                ...prev,
                birthYear,
                gender,
              }));
            }
            break;
          case "consultationNotes":
            if (onEditConsultationNotes) {
              await onEditConsultationNotes(consultationNotes);

              // 저장 성공 시 원본값 업데이트
              setOriginalValues((prev) => ({
                ...prev,
                consultationNotes,
              }));
            }
            break;
          case "memo":
            if (onEditMemo) {
              await onEditMemo(memo);

              // 저장 성공 시 원본값 업데이트
              setOriginalValues((prev) => ({
                ...prev,
                memo,
              }));
            }
            break;
          case "diseases":
            if (onEditDiseases) {
              await onEditDiseases(diseases);

              // 저장 성공 시 원본값 업데이트
              setOriginalValues((prev) => ({
                ...prev,
                diseases,
              }));
            }
            break;
          case "allergies":
            if (onEditAllergies) {
              await onEditAllergies(allergies);

              // 저장 성공 시 원본값 업데이트
              setOriginalValues((prev) => ({
                ...prev,
                allergies,
              }));
            }
            break;
        }
      } catch (error) {
        console.error(`저장 실패 (${fieldName}):`, error);
      } finally {
        // 저장 상태 제거
        setSavingFields((prev) => prev.filter((field) => field !== fieldName));
      }
    },
    [
      patient.id,
      birthYear,
      gender,
      consultationNotes,
      memo,
      diseases,
      allergies,
      onEditPatient,
      onEditConsultationNotes,
      onEditMemo,
      onEditDiseases,
      onEditAllergies,
      originalValues,
    ]
  );

  // debounced 자동저장 핸들러
  const handleDebouncedSave = useCallback(
    (fieldName: string) => {
      // 기존 타이머가 있으면 취소
      const existingTimeout = saveTimeouts.current.get(fieldName);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // 새 타이머 설정 (300ms 지연)
      const newTimeout = setTimeout(() => {
        performSave(fieldName);
        saveTimeouts.current.delete(fieldName);
      }, 300);

      saveTimeouts.current.set(fieldName, newTimeout);
    },
    [performSave]
  );

  // 필드 포커스 핸들러
  const handleFieldFocus = (fieldName: string) => {
    setEditingField(fieldName);
  };

  // 필드 블러 핸들러
  const handleFieldBlur = (fieldName: string) => {
    setEditingField(null);

    // 이미 저장 중이면 중복 저장 방지
    if (savingFields.includes(fieldName)) return;

    handleDebouncedSave(fieldName);
  };

  // 필드 변경 핸들러 (onChange에서는 상태만 업데이트)
  const handleFieldChange = (fieldName: string, value: string) => {
    // 상태 업데이트만 수행
    switch (fieldName) {
      case "birthYear":
        setBirthYear(value);
        break;
      case "gender":
        setGender(value);
        break;
      case "consultationNotes":
        setConsultationNotes(value);
        break;
      case "memo":
        setMemo(value);
        break;
      case "diseases":
        setDiseases(value);
        break;
      case "allergies":
        setAllergies(value);
        break;
    }
    // onChange에서는 저장하지 않음 - onBlur에서만 저장
  };

  // 환자 정보 수정 모달 핸들러
  const handleEditModalSubmit = (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => {
    if (onEditPatient) {
      // Patient 객체 형태로 변환
      const updatedPatient = {
        id: patient.id,
        birthDate: new Date(parseInt(patientData.birthYear), 0, 1).toISOString(),
        gender: patientData.gender,
      };

      onEditPatient(updatedPatient);
    }

    // 메모 업데이트 (별도 처리)
    if (onEditMemo && patientData.memo !== memo) {
      onEditMemo(patientData.memo);
    }

    setIsEditModalOpen(false);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  // AI 검토 시작 핸들러
  const handleAIReviewStart = () => {
    if (onAnalysisStart) {
      onAnalysisStart();
    }
  };

  return (
    <Container>
      <div style={{ textAlign: "right" }}>
        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
          최근 업데이트: {formatKoreanDate(patient.updatedAt)}
        </Text>
      </div>

      {/* 환자 기본 정보 - 헤더 영역 */}
      <PatientHeader>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Text textType="H2" color={COLOR_SYSTEM.get("Gray")[700]} element="h2">
            {patient.id ? `환자 ${patient.id}` : "신규 환자"}{" "}
            {age ? `(${age}세/${patient.gender || "-"})` : ""}
          </Text>

          {onExportMedicationHistory && (
            <Button
              variant="Outlined"
              color="Tertiary"
              size="Small"
              type="IconWithText"
              icon="Download"
              left
              onClick={onExportMedicationHistory}
              disabled={!(patient.prescriptions?.length || patient.otc?.length || patient.hff?.length)}
              disabledColor={!(patient.prescriptions?.length || patient.otc?.length || patient.hff?.length)}
              style={{ marginLeft: 8 }}
            >
              약력 다운로드
            </Button>
          )}

          <PatientOptionsContainer className="patient-options-container">
            <Button
              size="Small"
              type="Icon"
              variant="Ghost"
              color="Secondary"
              icon="Dots Vertical"
              onClick={() => setIsPatientOptionsOpen(!isPatientOptionsOpen)}
            />

            <PatientOptionsDropdown isOpen={isPatientOptionsOpen}>
              <PatientOptionItem
                onClick={() => {
                  setIsEditModalOpen(true);
                  setIsPatientOptionsOpen(false);
                }}
              >
                <Icon name="Edit" size={16} color={COLOR_SYSTEM.get("Gray")[600]} />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                  나이/성별 수정
                </Text>
              </PatientOptionItem>

              <PatientOptionItem
                onClick={() => {
                  handleFieldFocus("memo");
                  setTimeout(() => {
                    if (memoRef.current) {
                      memoRef.current.focus();
                      adjustTextareaHeight(memoRef.current);
                    }
                  }, 0);
                  setIsPatientOptionsOpen(false);
                }}
              >
                <Icon name="Edit" size={16} color={COLOR_SYSTEM.get("Gray")[600]} />
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                  메모 수정
                </Text>
              </PatientOptionItem>

              {onDeletePatient && (
                <PatientOptionItem
                  onClick={() => {
                    onDeletePatient();
                    setIsPatientOptionsOpen(false);
                  }}
                >
                  <Icon name="Trash" size={16} color={COLOR_SYSTEM.get("Red")[500]} />
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Red")[500]}>
                    환자 삭제
                  </Text>
                </PatientOptionItem>
              )}
            </PatientOptionsDropdown>
          </PatientOptionsContainer>
        </div>

        <div>
          {editingField === "memo" ? (
            <div>
              <Text
                textType="Body2"
                color={COLOR_SYSTEM.get("Gray")[500]}
                style={{ marginBottom: 4 }}
              >
                환자 식별용 메모: (AI에게는 제공되지 않는 정보입니다. 약사님이 환자를 기억하기 위한
                정보를 입력하세요.)
              </Text>
              <EditableTextarea
                ref={memoRef}
                value={memo}
                onChange={(e) => {
                  handleFieldChange("memo", e.target.value);
                  adjustTextareaHeight(e.target);
                }}
                onBlur={() => handleFieldBlur("memo")}
                placeholder="예: 예: 다제0712방문예정, 방문약료2"
                rows={1}
                spellCheck={false}
                style={{
                  border: `1px solid ${COLOR_SYSTEM.get("Skyblue")[300]}`,
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: COLOR_SYSTEM.get("Skyblue")[10],
                  width: "100%",
                }}
              />
            </div>
          ) : (
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Skyblue")[500]}
              style={{
                marginBottom: 8,
                cursor: "pointer",
                padding: "4px 0",
              }}
              onClick={() => {
                handleFieldFocus("memo");
                setTimeout(() => {
                  if (memoRef.current) {
                    memoRef.current.focus();
                    adjustTextareaHeight(memoRef.current);
                  }
                }, 0);
              }}
            >
              환자 식별용 메모: {memo || "클릭하여 메모를 입력하세요"}
              {savingFields.includes("memo") && (
                <span style={{ color: COLOR_SYSTEM.get("Blue")[500], marginLeft: 8 }}>
                  저장 중...
                </span>
              )}
            </Text>
          )}
        </div>
        {/* 방문 일정 */}
        {onCreateSchedule && onUpdateSchedule && onDeleteSchedule && (
          <ScheduleSection
            schedules={schedules || []}
            onCreateSchedule={onCreateSchedule}
            onUpdateSchedule={onUpdateSchedule}
            onDeleteSchedule={onDeleteSchedule}
          />
        )}
      </PatientHeader>

      {/* 그룹 1: 환자 정보 */}
      <SectionGroup>
        <SectionGroupTitle>환자 정보</SectionGroupTitle>

        {/* 질병 정보 */}
        <Card>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
              질병 정보{" "}
              {savingFields.includes("diseases") && (
                <span style={{ color: COLOR_SYSTEM.get("Blue")[500] }}>저장 중...</span>
              )}
            </Text>
            <InfoIconButton tooltip={<>환자의 질병 정보를 입력해주세요.<br/>모르는 경우 공란으로 두셔도 됩니다</>} />
          </SectionHeader>
          <InfoBox isEditing={editingField === "diseases"}>
            <EditableTextarea
              ref={diseasesRef}
              value={diseases}
              onChange={(e) => {
                handleFieldChange("diseases", e.target.value);
                adjustTextareaHeight(e.target);
              }}
              onFocus={(e) => {
                handleFieldFocus("diseases");
                adjustTextareaHeight(e.target);
              }}
              onBlur={() => handleFieldBlur("diseases")}
              placeholder="환자의 질병 정보를 입력하세요. (예: 고혈압, 제2형 당뇨병, 고지혈증)"
              rows={1}
              spellCheck={false}
            />
          </InfoBox>
        </Card>

        {/* 약물 부작용 및 알레르기 정보 */}
        <Card>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
              약물 부작용 및 알레르기 정보{" "}
              {savingFields.includes("allergies") && (
                <span style={{ color: COLOR_SYSTEM.get("Blue")[500] }}>저장 중...</span>
              )}
            </Text>
            <InfoIconButton tooltip={<>환자가 경험했거나 의심되는 약물관련 부작용 및 알레르기를 작성해주세요.<br/>모르는 경우 공란으로 두셔도 됩니다</>} />
          </SectionHeader>
          <InfoBox isEditing={editingField === "allergies"}>
            <EditableTextarea
              ref={allergiesRef}
              value={allergies}
              onChange={(e) => {
                handleFieldChange("allergies", e.target.value);
                adjustTextareaHeight(e.target);
              }}
              onFocus={(e) => {
                handleFieldFocus("allergies");
                adjustTextareaHeight(e.target);
              }}
              onBlur={() => handleFieldBlur("allergies")}
              placeholder="약물 부작용 및 알레르기 정보를 입력하세요. (예: 페니실린 - 발진, 아스피린 - 위장장애)"
              rows={1}
              spellCheck={false}
            />
          </InfoBox>
        </Card>

        {/* 추가 정보 */}
        <Card>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
              추가 정보{" "}
              {savingFields.includes("consultationNotes") && (
                <span style={{ color: COLOR_SYSTEM.get("Blue")[500] }}>저장 중...</span>
              )}
            </Text>
            <InfoIconButton tooltip={
              <>
                AI가 분석해주었으면 하는 모든 추가 정보를 입력하세요.<br/>
                없는 경우 공란으로 두셔도 됩니다<br/>
                • 최근 검사 수치 (혈압, 혈당, 콜레스테롤 등)<br/>
                • 환자가 궁금해 하는 정보<br/>
                • 생활습관, 식습관 정보<br/>
                • 최근의 증상 변화, 부작용 경험 등<br/>
                • 임신 여부, 임신 가능성, 수유 여부 등
              </>
            } />
          </SectionHeader>
          <InfoBox isEditing={editingField === "consultationNotes"}>
            <EditableTextarea
              ref={consultationNotesRef}
              value={consultationNotes}
              onChange={(e) => {
                handleFieldChange("consultationNotes", e.target.value);
                adjustTextareaHeight(e.target);
              }}
              onFocus={(e) => {
                handleFieldFocus("consultationNotes");
                adjustTextareaHeight(e.target);
              }}
              onBlur={() => handleFieldBlur("consultationNotes")}
              placeholder="분석을 위한 추가 정보를 입력하세요. (예: 혈압 140/90, 공복혈당 120, 어지러움 호소, 임신 중 or 가임기 여성)"
              rows={1}
              spellCheck={false}
            />
          </InfoBox>
        </Card>
      </SectionGroup>

      {/* 그룹 2: 의약품 */}
      <SectionGroup>
        <SectionGroupTitle>의약품</SectionGroupTitle>

        {/* 처방전 이력 */}
        <Card>
          <SectionHeader>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
                처방전 이력 <span style={{ color: COLOR_SYSTEM.get("Red")[500] }}>*</span>
              </Text>
              <InfoIconButton tooltip={<>현재 처방되어 복용하고 있는 약을 등록하세요.<br/>건강보험공단 엑셀파일 업로드 또는 직접 입력이 가능합니다.</>} />
            </div>
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              {onOpenHistoryViewer && prescriptionGroups && prescriptionGroups.length > 0 && (
                <Button
                  size="Small"
                  type="IconWithText"
                  variant="Outlined"
                  color="Secondary"
                  icon="Eye"
                  left
                  onClick={onOpenHistoryViewer}
                >
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                    처방이력 파악하기
                  </Text>
                </Button>
              )}
              {onToggleViewMode && prescriptionGroups && prescriptionGroups.length > 0 && (
                <Button
                  size="Small"
                  type="IconWithText"
                  variant="Outlined"
                  color="Secondary"
                  icon="Swap Vertical"
                  left
                  onClick={onToggleViewMode}
                >
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                    {hospitalGroupedPrescriptions ? "병원별 보기" : "날짜별 보기"}
                  </Text>
                </Button>
              )}
              {onEditPrescription && (
                <Button
                  size="Small"
                  type="IconWithText"
                  variant="Contained"
                  color="Primary"
                  icon="Plus"
                  left
                  onClick={() => onEditPrescription && onEditPrescription()}
                >
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[0]}>
                    새 처방전 추가
                  </Text>
                </Button>
              )}
            </div>
          </SectionHeader>
          {nhisRequests && nhisRequests.length > 0 && (
            <NhisRequestStatusList requests={nhisRequests} onCancelRequest={onCancelNhisRequest} />
          )}
          {prescriptionStats && prescriptionStats.uniqueCount > GAUGE_GREEN_MAX && (
            <div style={{ marginTop: "16px", marginBottom: "16px" }}>
              <MedicineCountGauge count={prescriptionStats.uniqueCount} />
            </div>
          )}
          {prescriptionGroups && prescriptionGroups.length > 0 ? (
            <>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", color: COLOR_SYSTEM.get("Gray")?.[400], display: "flex", alignItems: "center", gap: "8px" }}>
                {[
                  { icon: "Edit 01", label: "직접입력" },
                  { icon: "Document", label: "엑셀" },
                  { icon: "Camera", label: "촬영/OCR" },
                  { icon: "Building Filled", label: "심평원" },
                  { icon: "Image Check", label: "식별" },
                ].map(({ icon, label }) => (
                  <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                    <Icon name={icon} size={12} color={COLOR_SYSTEM.get("Gray")?.[400]} />
                    {label}
                  </span>
                ))}
              </span>
            </div>
            <PrescriptionHistory
              prescriptionGroups={prescriptionGroups}
              hospitalGroups={hospitalGroupedPrescriptions}
              referenceChecker={referenceChecker}
              onEditPrescription={onEditPrescription}
              onDeletePrescription={onDeletePrescription}
            />
            </>
          ) : (
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[300]}>
              등록된 처방전이 없습니다.
            </Text>
          )}
        </Card>

        {/* 일반의약품 이력 */}
        <Card>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
              일반의약품 이력
            </Text>
            <InfoIconButton tooltip={
              <>
                처방전 없이 약국에서 구매하여 복용 중인 일반의약품을 등록하세요.<br/>
                식약처 데이터베이스에서 검색하여 정확한 의약품 정보를 입력할 수 있습니다.<br/>
                식약처에 일반의약품으로 등록된 건강기능식품도 가능합니다(예: 아로나민 등)
              </>
            } />
            {onEditOtc && (
              <Button
                size="Small"
                type="IconWithText"
                variant="Contained"
                color="Primary"
                icon="Plus"
                left
                onClick={() => onEditOtc && onEditOtc()}
                style={{ marginLeft: "auto" }}
              >
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[0]}>
                  새 일반의약품 추가
                </Text>
              </Button>
            )}
          </SectionHeader>
          {otcList.length > 0 ? (
            <OTCHistory
              prescriptions={otcList}
              referenceChecker={referenceChecker}
              onEdit={onEditOtc}
              onDelete={onDeleteOtc}
              type="otc"
            />
          ) : (
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[300]}>
              등록된 일반의약품이 없습니다.
            </Text>
          )}
        </Card>

        {/* 건강기능식품 이력 */}
        <Card>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
              건강기능식품 이력
            </Text>
            <InfoIconButton tooltip={
              <>
                환자가 복용 중인 건강기능식품을 등록하세요.<br/>
                식약처 데이터베이스에서 검색하여 정확한 제품 정보를 입력할 수 있습니다.<br/>
                식약처에 등록되지 않은 제품(일반식품, 해외 직구 등)은 직접 입력이 가능합니다.
              </>
            } />
            {onEditHff && (
              <Button
                size="Small"
                type="IconWithText"
                variant="Contained"
                color="Primary"
                icon="Plus"
                left
                onClick={() => onEditHff && onEditHff()}
                style={{ marginLeft: "auto" }}
              >
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[0]}>
                  새 건강기능식품 추가
                </Text>
              </Button>
            )}
          </SectionHeader>
          {hffList.length > 0 ? (
            <OTCHistory
              prescriptions={hffList}
              referenceChecker={referenceChecker}
              onEdit={onEditHff}
              onDelete={onDeleteHff}
              type="hff"
            />
          ) : (
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[300]}>
              등록된 건강기능식품이 없습니다.
            </Text>
          )}
        </Card>

        {/* 식별 의약품 — 웹에서 추가 불가, 데이터 없으면 숨김 */}
        {(patient.drugIdentifications?.length ?? 0) > 0 && (
          <Card>
            <SectionHeader>
              <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
                식별 의약품
              </Text>
            </SectionHeader>
            <UnlinkedDrugIdentifications items={patient.drugIdentifications ?? []} onDelete={onDeleteDrugIdentification} />
          </Card>
        )}
      </SectionGroup>

      {/* 그룹 3: 약사 활동 내역 */}
      <SectionGroup>
        <SectionGroupTitle>약사 활동 내역</SectionGroupTitle>

        <Card style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
          {/* AI 검토 이력 */}
          <div>
            <SectionHeader>
              <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
                AI 검토 이력
              </Text>
            </SectionHeader>
            {/* 보고서 줄 오른쪽 체크리스트 칸 — 기록 유무는 환자 텍스트 기록 목록(reviewId·source)으로(결정 20) */}
            <AIReportHistoryList
              reports={patient.aiReviews ?? []}
              patientId={patient.id}
              textRecords={patient.consultationRecords}
              currentUserId={currentUserId}
              onWriteChecklist={onWriteChecklist}
              onOpenChecklist={onOpenChecklist}
            />
          </div>

          {/* 상담 녹음 기록 — 「AI 검토 이력」 아래, 녹음 기록만(텍스트 기록은 위 보고서 줄 칸으로 — 결정 20) */}
          {onSelectConsultationRecord && (
            <ConsultationRecordSection
              records={patient.consultationRecords || []}
              onSelectRecord={onSelectConsultationRecord}
            />
          )}
        </Card>
      </SectionGroup>

      {(onAnalysisStart || onAdminAnalysisStart) && (
        <FixedContainer>
          {onAdminAnalysisStart && editingField === null && (
            <FloatingButton
              icon="Document"
              onClick={onAdminAnalysisStart}
              size="Large"
              type="Icon"
              color="Tertiary"
            />
          )}
          {/* 기본 보고서 생성 버튼 */}
          {onAnalysisStart &&
            editingField === null &&
            (loadingProductLabel ? (
              <FloatingButton
                onClick={() => {
                  ToastManager.show({
                    content: "약물 정보를 불러오는 중입니다. 잠시만 기다려주세요.",
                  });
                }}
                size="XLarge"
                type="Text"
                color="Primary"
              >
                <span style={{ marginRight: 16 }}>
                  <Spinner size="Large" />
                </span>{" "}
                약물 정보를 확인중...
              </FloatingButton>
            ) : (
              <FloatingButton
                icon="Document"
                onClick={handleAIReviewStart}
                size="XLarge"
                type="IconWithText"
                color="Primary"
                left
              >
                 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>AI 검토</span>
                {!isPremiumUser && (
                  <span style={{ fontSize: '12px', lineHeight: '1', color: COLOR_SYSTEM.get("Yellow")[400] }}>(200P 차감)</span>
                )}
              </div>
              </FloatingButton>
            ))}
        </FixedContainer>
      )}

      {/* 환자 정보 수정 모달 */}
      <EditPatientModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSubmit={handleEditModalSubmit}
        initialData={{
          birthYear: patient.birthDate ? new Date(patient.birthDate).getFullYear().toString() : "",
          gender: patient.gender || "",
          memo: patient.memo || "",
        }}
      />
    </Container>
  );
};

export default PatientDetailTemplate;
